import type { AgentProgressEvent, AgentTimelineEntry } from '~/components/AiChat/AgentTimeline/types';
import { calculateEventsDuration } from '~/components/AiChat/utils/timelineUtils';
import {
  foldProgressEvents,
  getProgressMetadata,
  resolveRowStatus,
} from '~/components/ProgressTimeline/progressTimelineUtils';
import type { ProgressEventHandlers, ProgressStatus } from '~/components/ProgressTimeline/types';
import { PROGRESS_EVENT_KIND } from '~/components/ProgressTimeline/types';

import type { FnolRunState, ProgressKind } from './fnolProgressTimelineTypes';
import { PROGRESS_KINDS } from './fnolProgressTimelineTypes';

/** Channel the FNOL processing run publishes its progress events on. */
export const FNOL_PROGRESS_TOPIC = 'fnol_progress';

const isProgressEvent = (event: AgentProgressEvent): boolean =>
  event.topic === FNOL_PROGRESS_TOPIC && PROGRESS_KINDS.includes(getProgressMetadata(event).kind as ProgressKind);

const isKind = (kind: ProgressKind) => (event: AgentProgressEvent) => getProgressMetadata(event).kind === kind;

// ISO 8601 timestamps sort lexicographically, so a plain string compare orders them chronologically
// without allocating a Date — or paying for locale-sensitive collation, which is free to reorder the
// punctuation these strings are full of.
const latestOf = (events: AgentProgressEvent[]): AgentProgressEvent | undefined =>
  events.reduce<AgentProgressEvent | undefined>(
    (latest, event) => (!latest || event.timestamp > latest.timestamp ? event : latest),
    undefined
  );

const earliestOf = (events: AgentProgressEvent[]): AgentProgressEvent | undefined =>
  events.reduce<AgentProgressEvent | undefined>(
    (earliest, event) => (!earliest || event.timestamp < earliest.timestamp ? event : earliest),
    undefined
  );

/**
 * Narrow a topic's events to the newest run on it. A retry publishes a fresh `plan`, and the
 * superseded attempt's events are still in the buffer — they must not advance the new run's rows,
 * inflate its duration, or report it finished on the previous attempt's terminal event.
 */
const inCurrentRun = (topicEvents: AgentProgressEvent[]): AgentProgressEvent[] => {
  const plan = latestOf(topicEvents.filter(isKind(PROGRESS_EVENT_KIND.PLAN)));
  return plan ? topicEvents.filter((event) => event.timestamp >= plan.timestamp) : topicEvents;
};

// Both are free text and either can arrive null *or* empty, so neither a null check nor a truthiness
// check on one field alone is enough.
const getEventText = (event: AgentProgressEvent): string => event.message?.trim() || event.title?.trim() || '';

/**
 * The run's latest human-readable line, for the caller to show alongside (or instead of) the rows.
 *
 * A failure wins over anything more recent: the workers have independent clocks, so a straggling
 * update can carry a later timestamp than the terminal event and would otherwise bury the reason.
 * Beyond that this reads every event on the topic, not just the four checklist kinds — the ungrouped
 * updates in between are exactly the free text worth showing while the checklist is still empty.
 */
export const toLatestStatus = (events: AgentProgressEvent[]): ProgressStatus | undefined => {
  const runEvents = inCurrentRun(events.filter((event) => event.topic === FNOL_PROGRESS_TOPIC));
  const spokenEvents = runEvents.filter(getEventText);

  const latest = latestOf(spokenEvents.filter(isKind(PROGRESS_EVENT_KIND.FAILED))) ?? latestOf(spokenEvents);
  const started = earliestOf(runEvents);
  if (!latest || !started) {
    return undefined;
  }

  return {
    text: getEventText(latest),
    // Both bounds come from every event in the run, including the silent ones: the `plan` that starts
    // it carries no text of its own, so anchoring on the first *spoken* event would lose the opening
    // stretch of the run.
    startedAt: started.timestamp,
    durationSeconds: calculateEventsDuration(runEvents),
    isRunning: !runEvents.some(
      (event) => isKind(PROGRESS_EVENT_KIND.COMPLETED)(event) || isKind(PROGRESS_EVENT_KIND.FAILED)(event)
    ),
  };
};

const FNOL_HANDLERS: ProgressEventHandlers<FnolRunState> = {
  [PROGRESS_EVENT_KIND.STEP_STARTED]: (state, _event, meta) => {
    state.activeIndex = Math.max(state.activeIndex, meta.index ?? 0);
  },
  [PROGRESS_EVENT_KIND.COMPLETED]: (state) => {
    state.isCompleted = true;
  },
  // The latest by timestamp; on a tie the first to arrive stays.
  [PROGRESS_EVENT_KIND.FAILED]: (state, event) => {
    if (!state.failure || event.timestamp > state.failure.timestamp) state.failure = event;
  },
};

/**
 * Reduce a task's real-time events to the timeline rows for one run.
 *
 * The plan event declares every row up front, so the checklist renders complete from the first event
 * rather than growing row by row. `step_started(i)` implies every earlier row is done, which is what
 * lets a dropped or skipped step self-heal, and `completed` resolves whatever is left — without it
 * the last row would never finish.
 *
 * The active row is the *highest* index seen, never the most recent: a task retry re-runs the
 * producer from the top and re-fires every emit, so anything else walks the checklist backward.
 *
 * Rows are ordered by the plan's `index`, never by timestamp — events come from up to three workers
 * with independent clocks.
 */
export const toTimelineEntries = (events: AgentProgressEvent[]): AgentTimelineEntry[] => {
  const progressEvents = events.filter(isProgressEvent);

  const plan = latestOf(progressEvents.filter(isKind(PROGRESS_EVENT_KIND.PLAN)));
  const planSteps = plan && getProgressMetadata(plan).steps;
  if (!plan || !planSteps?.length) {
    return [];
  }

  const currentRunEvents = inCurrentRun(progressEvents);

  const { activeIndex, isCompleted, failure } = foldProgressEvents<FnolRunState>(
    currentRunEvents,
    // Receiving the plan already implies row 0 is in progress, hence the 0 floor.
    { activeIndex: 0, isCompleted: false },
    FNOL_HANDLERS
  );
  // The failure detail (e.g. the policy-search report) is strictly more informative than the row
  // label, so it becomes the row's expandable body.
  const failureContent = failure ? getProgressMetadata(failure).detail || failure.message || undefined : undefined;

  const orderedSteps = [...planSteps].sort((a, b) => a.index - b.index);
  const failedStepIndex = orderedSteps.findIndex(
    (step) => step.key === (failure && getProgressMetadata(failure).step_key)
  );

  return orderedSteps.map((step, position) => ({
    id: step.key,
    title: step.title,
    status: resolveRowStatus({
      position,
      activeIndex,
      isDone: isCompleted,
      failedIndex: failedStepIndex >= 0 ? failedStepIndex : undefined,
    }),
    ...(position === failedStepIndex ? { content: failureContent } : {}),
  }));
};
